// src/pages/AdminLogin.jsx
import React, { useState, useEffect } from "react";
import { Eye, EyeSlash } from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
const API = process.env.REACT_APP_API_URL;
const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();

  // Forgot Password States
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1: email, 2: otp, 3: new password
  const [resetOtp, setResetOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Countdown starts ONLY on resend click
  const [countdown, setCountdown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(""), 100000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const startCountdown = () => {
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Normal Login - Send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Please enter email and password");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Updated URL
      const response = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Invalid credentials");

      setOtpSent(true);
      setSuccess("OTP sent successfully!");
      // Do NOT start countdown here
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Verify Login OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp) {
      setError("Please enter the OTP");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API}/api/admin/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Invalid OTP");

      localStorage.setItem("adminToken", data.token);
      localStorage.setItem("admin", JSON.stringify(data.admin));
      navigate("/admin");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP (Login) - Only starts countdown here
  const handleResendOtp = async () => {
    if (countdown > 0) return;
    setResendLoading(true);
    setError("");

    try {
      // Updated URL
      const response = await fetch(`${API}/api/admin/resend-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      setSuccess("New OTP sent!");
      startCountdown(); // ← Countdown starts only on resend
    } catch (err) {
      setError(err.message);
    } finally {
      setResendLoading(false);
    }
  };

  // Step 1: Send Reset OTP
  const handleSendResetOtp = async (e) => {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Updated URL
      const response = await fetch(`${API}/api/admin/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      setResetStep(2);
      setSuccess("Reset OTP sent to your email!");
      // Do NOT start countdown on initial send
    } catch (err) {
      setError(err.message || "Failed to send reset OTP");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify Reset OTP (uses same endpoint)
  const handleVerifyResetOtp = async (e) => {
    e.preventDefault();
    if (!resetOtp || resetOtp.length !== 6) {
      setError("Please enter a valid 6-digit OTP");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Updated URL
      const response = await fetch(`${API}/api/admin/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp: resetOtp, resetMode: true }), // ← Tell backend it's reset
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Invalid or expired OTP");
      }

      if (data.resetVerified) {
        setResetStep(3);
        setSuccess("OTP verified! Now set your new password.");
      } else {
        setError("Invalid OTP for password reset");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Resend Reset OTP - Only starts countdown on click
  const handleResendResetOtp = async () => {
    if (countdown > 0) return;
    setResendLoading(true);
    setError("");

    try {
      // Updated URL
      const response = await fetch(`${API}/api/admin/resend-reset-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      setSuccess("New reset OTP sent!");
      startCountdown(); // ← Only starts here
    } catch (err) {
      setError(err.message);
    } finally {
      setResendLoading(false);
    }
  };

  // Step 3: Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) {
      setError("Both password fields are required");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Updated URL
      const response = await fetch(`${API}/api/admin/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp: resetOtp, newPassword }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      setSuccess("Password reset successfully! Redirecting to login...");
      setTimeout(() => {
        setIsForgotPassword(false);
        setResetStep(1);
        setResetOtp("");
        setNewPassword("");
        setConfirmPassword("");
        setError("");
        setSuccess("");
        setCountdown(0);
      }, 2000);
    } catch (err) {
      setError(err.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const resetAll = () => {
    setIsForgotPassword(false);
    setResetStep(1);
    setOtpSent(false);
    setOtp("");
    setResetOtp("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
    setSuccess("");
    setCountdown(0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black to-gray-900 flex items-center justify-center p-5 font-sans">
      <div className="bg-white w-full max-w-md rounded-2xl p-10 shadow-2xl text-center">
        <h2 className="text-3xl font-extrabold mb-8 text-gray-900">
          <span className="text-red-600 italic">Medi</span>Store Admin Login
        </h2>

        {success && (
          <p className="text-green-600 text-sm font-medium mb-4 animate-pulse">{success}</p>
        )}

        {isForgotPassword ? (
          <>
            {resetStep === 1 && (
              <form onSubmit={handleSendResetOtp}>
                <p className="text-gray-600 mb-6 text-left">Enter your email to reset password</p>
                <div className="mb-5">
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-xl text-base focus:outline-none focus:border-red-600 focus:shadow-lg focus:shadow-red-600/20 transition"
                    required
                    disabled={loading}
                  />
                </div>

                {error && <p className="text-red-600 text-sm font-medium mb-4 text-left">{error}</p>}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-br from-red-600 to-red-700 text-white py-3.5 rounded-xl font-semibold text-base shadow-lg shadow-red-600/30 hover:-translate-y-1 hover:shadow-xl hover:shadow-red-600/40 transition disabled:opacity-70"
                >
                  {loading ? "Sending..." : "Send Reset OTP"}
                </button>

                <button type="button" onClick={resetAll} className="mt-4 text-red-600 text-sm underline">
                  Back to Login
                </button>
              </form>
            )}

            {resetStep === 2 && (
              <form onSubmit={handleVerifyResetOtp}>
                <p className="text-gray-600 mb-5 text-left">
                  {/* OTP sent to <strong>{email}</strong> */}
                </p>

                <div className="mb-6">
                  <input
                    type="text"
                    placeholder="Enter 6-digit OTP"
                    value={resetOtp}
                    onChange={(e) => setResetOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    maxLength="6"
                    className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-xl text-base focus:outline-none focus:border-red-600 focus:shadow-lg focus:shadow-red-600/20 transition text-center tracking-widest text-2xl"
                    required
                    disabled={loading}
                    autoFocus
                  />
                </div>

                {error && <p className="text-red-600 text-sm font-medium mb-4 text-left">{error}</p>}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-br from-red-600 to-red-700 text-white py-3.5 rounded-xl font-semibold text-base shadow-lg shadow-red-600/30 hover:-translate-y-1 hover:shadow-xl hover:shadow-red-600/40 transition disabled:opacity-70"
                >
                  {loading ? "Verifying..." : "Verify OTP"}
                </button>

                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={handleResendResetOtp}
                    disabled={resendLoading || countdown > 0}
                    className="text-red-600 text-sm underline disabled:text-gray-500 disabled:no-underline"
                  >
                    {resendLoading
                      ? "Sending..."
                      : countdown > 0
                      ? `Resend OTP in ${countdown}s`
                      : "Resend OTP"}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => setResetStep(1)}
                  className="w-full mt-4 bg-gray-100 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-200 transition"
                >
                  Back
                </button>
              </form>
            )}

            {resetStep === 3 && (
              <form onSubmit={handleResetPassword}>
                <p className="text-gray-600 mb-5 text-left">Set your new password</p>

                <div className="mb-5 relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    placeholder="New Password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-xl text-base focus:outline-none focus:border-red-600 focus:shadow-lg focus:shadow-red-600/20 transition pr-12"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-red-600"
                  >
                    {showNewPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                <div className="mb-6 relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm New Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-xl text-base focus:outline-none focus:border-red-600 focus:shadow-lg focus:shadow-red-600/20 transition pr-12"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-red-600"
                  >
                    {showConfirmPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                {error && <p className="text-red-600 text-sm font-medium mb-4 text-left">{error}</p>}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-br from-red-600 to-red-700 text-white py-3.5 rounded-xl font-semibold text-base shadow-lg shadow-red-600/30 hover:-translate-y-1 hover:shadow-xl hover:shadow-red-600/40 transition disabled:opacity-70"
                >
                  {loading ? "Resetting..." : "Set New Password"}
                </button>

                <button
                  type="button"
                  onClick={() => setResetStep(2)}
                  className="w-full mt-4 bg-gray-100 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-200 transition"
                >
                  Back
                </button>
              </form>
            )}
          </>
        ) : (
          <>
            {!otpSent ? (
              <form onSubmit={handleSendOtp}>
                <div className="mb-5">
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-xl text-base focus:outline-none focus:border-red-600 focus:shadow-lg focus:shadow-red-600/20 transition"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="mb-5 relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-xl text-base focus:outline-none focus:border-red-600 focus:shadow-lg focus:shadow-red-600/20 transition pr-12"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full p-1.5 transition"
                  >
                    {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                <div className="text-right mb-5">
                  <button
                    type="button"
                    onClick={() => {
                      setIsForgotPassword(true);
                      setResetStep(1);
                      setError("");
                      setSuccess("");
                    }}
                    className="text-red-600 text-sm font-medium hover:underline"
                  >
                    Forgot Password?
                  </button>
                </div>

                {error && <p className="text-red-600 text-sm font-medium mb-4 text-left">{error}</p>}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-br from-red-600 to-red-700 text-white py-3.5 rounded-xl font-semibold text-base shadow-lg shadow-red-600/30 hover:-translate-y-1 hover:shadow-xl hover:shadow-red-600/40 transition disabled:opacity-70"
                >
                  {loading ? "Sending OTP..." : "Send OTP"}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp}>
                <p className="text-gray-600 mb-5 text-left">
                  {/* OTP sent to <strong>{email}</strong> */}
                </p>

                <div className="mb-6">
                  <input
                    type="text"
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    maxLength="6"
                    className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-xl text-base focus:outline-none focus:border-red-600 focus:shadow-lg focus:shadow-red-600/20 transition text-center tracking-widest text-2xl"
                    required
                    disabled={loading}
                    autoFocus
                  />
                </div>

                {error && <p className="text-red-600 text-sm font-medium mb-4 text-left">{error}</p>}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-br from-red-600 to-red-700 text-white py-3.5 rounded-xl font-semibold text-base shadow-lg shadow-red-600/30 hover:-translate-y-1 hover:shadow-xl hover:shadow-red-600/40 transition disabled:opacity-70"
                >
                  {loading ? "Verifying..." : "Verify OTP & Login"}
                </button>

                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={resendLoading || countdown > 0}
                    className="text-red-600 text-sm underline disabled:text-gray-500 disabled:no-underline"
                  >
                    {resendLoading
                      ? "Sending..."
                      : countdown > 0
                      ? `Resend OTP in ${countdown}s`
                      : "Resend OTP"}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setOtpSent(false);
                    setOtp("");
                    setError("");
                  }}
                  disabled={loading}
                  className="w-full mt-4 bg-gray-100 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-200 transition"
                >
                  Back to Login
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminLogin;